import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { TitleStrategy, type RouterStateSnapshot } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Subject, distinctUntilChanged, switchMap } from 'rxjs';

/**
 * Puts the *translated* route title in the browser tab.
 *
 * Angular's default strategy writes `routeMeta.title` verbatim, so a route that
 * names an i18n key showed the key itself — `nav.secondary.time.employments`
 * where "Dienstverhältnisse" belonged. Nothing translated titles before this;
 * the workspace had no `TitleStrategy` at all.
 *
 * `selectTranslate` rather than `translate`: it waits for the active language's
 * file and emits again when the language changes, so switching to English also
 * relabels the tab. A plain `translate` would return whatever was loaded at
 * navigation time and then stay wrong.
 */
@Injectable()
export class TranslatedTitleStrategy extends TitleStrategy {
  readonly #title = inject(Title);
  readonly #transloco = inject(TranslocoService);
  readonly #destroyRef = inject(DestroyRef);

  /** The key of the route currently shown; one subscription follows it. */
  readonly #key = new Subject<string>();

  constructor() {
    super();
    this.#key
      .pipe(
        distinctUntilChanged(),
        switchMap((key) => this.#transloco.selectTranslate(key)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((translated: string) => this.#title.setTitle(translated));
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const title = this.buildTitle(snapshot);
    // A route without a title leaves the previous one alone, as Angular's own
    // strategy does — clearing it would blank the tab mid-navigation.
    if (title !== undefined) {
      this.#key.next(title);
    }
  }
}
